import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCheckoutState } from '~/features/checkout/hooks/useCheckoutState';
import useBasket from '~/features/commerce-api/hooks/useBasket';
import { BetterStackLogLevel, clientLog } from '~/lib/better-stack';
import { ApplePayEvents, ApplePaySuccessResponse } from '~/services/altapay/integrations/apple-pay';
import {
    clientRedirect,
    createAltaPayCallbackUrl,
    extractPaymentInstrumentByMethod,
} from '~/services/altapay/lib/utils';
import { PAYMENT_DISPLAY_PREFIX } from '~/services/altapay/constants';
import { useAltapaySession } from '~/services/altapay/hooks';
import { isMissingBasketError } from '~/services/altapay/lib/errors';
import AltaPayPayment, { AltaPayPaymentType } from '~/services/altapay/payment';
import { generateAltaPayApiUrl } from '~/services/altapay/session';
import { AltaPay, CreditCardMessage } from '~/services/altapay/types';
import { Text } from '~/shared/components';
import Spinner from '~/shared/components/Spinner/Spinner';
import Typography from '~/shared/components/Typography/Typography';
import { PaymentMethod } from '~/shared/constants';
import { useAsyncPolling } from '~/shared/hooks/useAsyncPolling';
import { useFrame } from '~/shared/utils';
import { parseError } from '~/shared/utils/parse-error';
import { useTranslation } from '~/shared/utils/translation';
import {
    StyledCheckoutAside,
    StyledCheckoutMain,
    StyledCheckoutMobileSubmit,
    StyledCheckoutWrapper,
} from '~/templates/pages/components/CheckOutLayout/styled';
import { CheckoutSummary } from '../CheckoutSummary/CheckoutSummary';
import { PaymentMessageBox } from './components/MessageBox/MessageBox';
import { PaymentOptions } from './components/PaymentOptions/PaymentOptions';
import {
    useBasePaymentOptions,
    useDefaultOrderFlow,
    useGetOrderNumber,
    usePaymentInitiationError,
    useSetPaymentInstrument,
} from './hooks';
import {
    isAltaPayApplePay,
    isAltaPayPaymentOption,
    isAltaPayScriptPayment,
    SelectablePaymentOption,
} from './types';
import styles from './Payment.module.scss';

type Loading = { isLoading: true };
type LoadingError = { error: unknown };
type LoadableData<DataType> = Loading | DataType | LoadingError | undefined;

const isLoadingData = <DataType,>(loadableData: LoadableData<DataType>): loadableData is Loading =>
    !!(loadableData && (loadableData as Loading).isLoading);

const isLoadingError = <DataType,>(
    loadableData: LoadableData<DataType>,
): loadableData is LoadingError => !!(loadableData && (loadableData as LoadingError).error);

const PaymentFlow: React.FC = () => {
    const { data: frame } = useFrame();
    const { translate } = useTranslation();
    const { orderNumber: generatedOrderNumber, setOrderNumber } = useCheckoutState();
    const basket = useBasket();

    const { basketId, paymentInstruments: currentPaymentInstruments, isFreeOrder } = basket;

    const { hasAltaPay } = useBasePaymentOptions();

    // 1. Initialize session with Altapay if we have AltaPay options.
    const {
        sessionId,
        syncSessionWithBasket,
        isUpdatingSession: isUpdatingAltaPaySession,
    } = useAltapaySession({ enabled: hasAltaPay && !isFreeOrder });

    const boundScripts = useRef<string[]>([]);

    // We need our AltaPay session to have all the necessary data before fetching payment options.
    const [hasUpdatedAltaPaySession, setHasUpdatedAltaPaySession] = useState(false);

    const [initiatedPayment, setInitiatedPayment] =
        useState<LoadableData<AltaPay.Schemas['Payment']>>();
    const hasInitiatedPayment = isLoadingData(initiatedPayment);

    const [scriptUrl, setScriptUrl] = useState<string>();

    const [selectedOption, setSelectedOption] = useState<SelectablePaymentOption>();
    const [redirectUrl, setRedirectUrl] = useState<string>();

    const redirectTo = (path: string, options?: { immediately?: boolean }) => {
        setRedirectUrl(path);

        if (options?.immediately) {
            clientRedirect({ targetUrl: path });
            return;
        }

        /**
         * We will add a momentary delay prior to being redirected to successfully display
         * the redirect message before navigating to a different page.
         */
        setTimeout(() => clientRedirect({ targetUrl: path }), 1500);
    };

    const { startDefaultOrderFlow, startFreeOrderFlow, isGeneratingOrderFlowLinks } =
        useDefaultOrderFlow();
    const { setPaymentInstrument, error: paymentInstrumentError } = useSetPaymentInstrument();
    const { prepareOrderNumber, error: orderNumberGenerationError } = useGetOrderNumber();
    const { handlePaymentInitiationError } = usePaymentInitiationError();

    // 2. Start the free order flow, or prepare the order number for AltaPay.
    useEffect(() => {
        if (isFreeOrder) {
            startFreeOrderFlow()
                .then(redirectTo)
                .catch((error) => setInitiatedPayment({ error }));
        } else if (hasAltaPay) {
            prepareOrderNumber();
        }
    }, []);

    const updatePaymentInformation = async (shouldUseCurrentBasket?: boolean) => {
        if (!generatedOrderNumber) return;

        const setAltaPaySession = async () => {
            const callbackUrl = createAltaPayCallbackUrl({
                orderNo: generatedOrderNumber,
            });

            await syncSessionWithBasket(
                generatedOrderNumber,
                {
                    callbacks: {
                        /**
                         * 'FUNCTION' means that it will use the 'redirect' URL and allow us to handle
                         * the payment result ourselves.
                         */
                        success: { type: 'FUNCTION' },
                        failure: { type: 'FUNCTION' },
                        redirect: callbackUrl.toString(),
                    },
                },
                shouldUseCurrentBasket,
            );
        };

        const updatePaymentInstruments = () => {
            const altaPayInstrument = extractPaymentInstrumentByMethod(
                currentPaymentInstruments,
                PaymentMethod.ALTAPAY,
            );

            if (altaPayInstrument) {
                // Re-set payment instrument to AltaPay to update amount.
                return setPaymentInstrument({ paymentMethod: PaymentMethod.ALTAPAY });
            }
        };

        // 1. Set AltaPay checkout session on load.
        await setAltaPaySession();

        // 2. Update payment instruments, if needed, on load and when total changes.
        await updatePaymentInstruments();
    };

    // 3. Update AltaPay checkout session and payment instrument on load.
    useEffect(() => {
        /**
         * This only applies when AltaPay options are active.
         * We will need to make sure that the basket total constantly reflects
         * on the checkout session and the payment instrument.
         */
        if (!hasAltaPay || !sessionId || !generatedOrderNumber || hasUpdatedAltaPaySession) return;

        // We will initially use the current basket data to load payment methods faster.
        updatePaymentInformation(true).then(() => {
            setHasUpdatedAltaPaySession(true);
        });
    }, [generatedOrderNumber, sessionId]);

    /**
     * #APPLEPAY
     * 3.1. When Apple Pay is selected, constantly poll for basket updates and update session if needed.
     * If a payment has been initiated, we will stop the polling.
     */
    useAsyncPolling(updatePaymentInformation, {
        enabled:
            hasUpdatedAltaPaySession && !hasInitiatedPayment && isAltaPayApplePay(selectedOption),
    });

    // 5. Create payment request to selected service.
    const createPaymentRequest = async (paymentOption: SelectablePaymentOption) => {
        if (!paymentOption?.id || hasInitiatedPayment) return;

        setInitiatedPayment({ isLoading: true });

        try {
            console.log(
                `5A. Created payment request with payment option ${paymentOption.name} (${paymentOption.id}).`,
            );

            if (isAltaPayPaymentOption(paymentOption)) {
                if (!generatedOrderNumber) throw new Error();

                const payment = new AltaPayPayment(paymentOption);

                /**
                 * We will not call this when using Apple Pay as it will cause an
                 * InvalidAccessError. This is because Apple Pay requires its initialization
                 * to not come before async functions.
                 *
                 * Instead, we will do polling when Apple Pay is selected. (See #APPLEPAY).
                 */
                if (!isAltaPayApplePay(paymentOption)) {
                    let sessionData: AltaPay.Schemas['Session'] | undefined = undefined;

                    /**
                     * There's no need to bind this information multiple times for each payment option.
                     */
                    const isAlreadyBound = boundScripts.current.includes(paymentOption.id);

                    /**
                     * Options that utilize scripts are embedded and need an element ID to bind to.
                     * So, we'll update the session configuration and provide the element IDs of our
                     * iframe containers dynamically
                     */
                    if (!isAlreadyBound && isAltaPayScriptPayment(paymentOption)) {
                        sessionData = {
                            callbacks: {
                                /**
                                 * 'formStyling' contains the markup and the styles for our credit card form.
                                 * It returns the contents of /public/html/form-<method>.html.
                                 */
                                formStyling: generateAltaPayApiUrl(
                                    `/api/altapay/form?paymentType=${AltaPayPayment.getPaymentType(paymentOption)}`,
                                ).toString(),
                            },
                            configuration: {
                                paymentDisplayType: 'SCRIPT',
                                paymentDisplayComponent: `${PAYMENT_DISPLAY_PREFIX}-${paymentOption.id}`,
                            },
                        };

                        boundScripts.current.push(paymentOption.id);
                    }

                    await syncSessionWithBasket(generatedOrderNumber, sessionData);
                }

                const [_, paymentRequest] = await Promise.all([
                    setPaymentInstrument({ paymentMethod: PaymentMethod.ALTAPAY }),
                    payment.initiate(generatedOrderNumber, basket),
                ]);

                if (!paymentRequest) throw new Error('AltaPay payment request failed.');

                switch (paymentRequest._type) {
                    case AltaPayPaymentType.APPLEPAY: {
                        const { session: applePaySession } = paymentRequest;

                        setInitiatedPayment({ isLoading: true });

                        applePaySession.addEventListener(ApplePayEvents.SUCCESS, (event) => {
                            const { detail: data } = event as CustomEvent<ApplePaySuccessResponse>;

                            console.log(`4.2A. ApplePay payment completed.`, data);

                            const redirectToDefaultCallback = () => {
                                const redirectData = btoa(
                                    JSON.stringify({
                                        paymentId: data.paymentId,
                                    }),
                                );

                                redirectTo(
                                    createAltaPayCallbackUrl({
                                        orderNo: generatedOrderNumber,
                                        sessionId,
                                        redirectData,
                                    }).toString(),
                                );
                            };

                            if (data?.resultUrl) {
                                redirectTo(data.resultUrl);
                            } else {
                                // If a redirect URL is not provided, let's redirect to our default callback.
                                redirectToDefaultCallback();
                            }
                        });

                        applePaySession.addEventListener(ApplePayEvents.CANCEL, () => {
                            // Reset initiated payment.
                            setInitiatedPayment(undefined);

                            console.log(`4.2B. ApplePay payment cancelled.`);
                        });

                        applePaySession.addEventListener(ApplePayEvents.ERROR, (event) => {
                            const { detail: apError } = event as CustomEvent<unknown>;

                            clientLog({
                                event: 'AltaPay:Checkout:ApplePayError',
                                level: BetterStackLogLevel.NOTICE,
                                message: `Technical errors encountered with ApplePay payment for order ${generatedOrderNumber}.`,
                                data: {
                                    error: parseError(apError),
                                    orderNumber: generatedOrderNumber,
                                    basketId,
                                    sessionId,
                                },
                            });

                            // Reset initiated payment.
                            setInitiatedPayment(undefined);

                            console.log(`4.2C. ApplePay payment error.`, apError);
                        });

                        break;
                    }

                    case AltaPayPaymentType.CREDIT_CARD:
                    case AltaPayPaymentType.MOBILEPAY:
                    case AltaPayPaymentType.KLARNA: {
                        const { data } = paymentRequest;

                        console.log(`4.2B. Payment request created: ${data.paymentId}`);

                        setScriptUrl(undefined);

                        // Start processing payment request.
                        processPaymentRequest(data, paymentRequest._type);
                        setInitiatedPayment(data);

                        break;
                    }
                }
            } else {
                console.log(
                    `5B. Created payment request with payment option ${paymentOption.name} (${paymentOption.id}).`,
                );

                await setPaymentInstrument({ paymentMethod: PaymentMethod.NETS });

                console.log(`6A.1B. Payment method has been set to ${PaymentMethod.NETS}.`);

                const destinationUrl = await startDefaultOrderFlow();

                redirectTo(destinationUrl);
            }
        } catch (error) {
            console.error(error);

            setInitiatedPayment({ error });
            handlePaymentInitiationError(error, paymentOption);
        }
    };

    // 4A. Handle payment selection of user.
    const onPaymentChange = (paymentOption: SelectablePaymentOption) => {
        setSelectedOption(paymentOption);

        console.log(`4A. User has selected ${paymentOption.name} (${paymentOption.id}).`);

        if (isAltaPayScriptPayment(paymentOption)) {
            console.log(
                `4A.1A. Script payment selected. Payment will be INITIATED to get script for iframe window.`,
            );

            createPaymentRequest(paymentOption);
        }
    };

    // 4B. Handle form submission for applicable payment methods.
    const onFormSubmission = () => {
        if (!selectedOption) return;

        console.log(
            `4B. User has submitted form using ${selectedOption.name} (${selectedOption.id}).`,
        );

        createPaymentRequest(selectedOption);
    };

    // 6. Process instructions from initiated payment request.
    const processPaymentRequest = (
        paymentRequest: AltaPay.Schemas['Payment'],
        paymentType?: AltaPayPaymentType,
    ) => {
        const { type, url } = paymentRequest;

        console.log(
            `5. Processing payment request of type ${type} and transaction ID ${paymentRequest.paymentId}.`,
        );

        switch (type) {
            case 'REDIRECT':
                console.log(`5A.1. Payment request type REDIRECT. Redirecting to ${url}.`);

                // We will redirect immediately for MobilePay due to iOS' strict timing requirements after click (~3s).
                redirectTo(url, { immediately: paymentType === AltaPayPaymentType.MOBILEPAY });
                break;

            case 'SCRIPT': {
                console.log(`5B.1. Payment request type SCRIPT. Setting payment script to ${url}.`);
                setScriptUrl(url);
                break;
            }
        }
    };

    // 7. Process completed AltaPay script payment.
    const onAltaPayScriptComplete = useCallback(
        (transaction: CreditCardMessage) => {
            console.log('7. Handle completion of AltaPay script option.');

            const redirectData = btoa(
                JSON.stringify({
                    paymentId: transaction.transaction_info.checkout_payment_id,
                }),
            );

            const callbackUrl = createAltaPayCallbackUrl({
                orderNo: transaction.shop_orderid,
                redirectData,
                sessionId,
            });

            setInitiatedPayment({ isLoading: true });
            redirectTo(callbackUrl.toString());
        },
        [generatedOrderNumber, sessionId],
    );

    const messageBox: React.ReactNode = useMemo(() => {
        if (isGeneratingOrderFlowLinks)
            return (
                <PaymentMessageBox
                    headline={translate('checkout.payment.messages.orderCreationTitle')}
                    bodyText={translate('checkout.payment.messages.orderCreationText')}
                    isLoading
                />
            );

        if (redirectUrl)
            return (
                <PaymentMessageBox
                    headline={translate('checkout.payment.messages.redirectTitle')}
                    bodyText={translate('checkout.payment.messages.redirectText')}
                    isLoading
                />
            );

        if (orderNumberGenerationError)
            return (
                <PaymentMessageBox
                    headline={translate(
                        'checkout.payment.messages.orderNumberGenerationErrorTitle',
                    )}
                    bodyText={translate('checkout.payment.messages.orderNumberGenerationErrorText')}
                    cta={{
                        text: translate(
                            'checkout.payment.messages.orderNumberGenerationErrorRetryText',
                        ),
                        onClick: () => {
                            // Let's try to remove the stored order number as it's the most common cause of error (409).
                            setOrderNumber(undefined);
                            // Reload page afterwards.
                            window.location.reload();
                        },
                    }}
                />
            );

        const hasErrorOnPaymentInitiation = isLoadingError(initiatedPayment);
        const isBasketMissing =
            hasErrorOnPaymentInitiation && isMissingBasketError(initiatedPayment.error);

        if (isBasketMissing)
            return (
                <PaymentMessageBox
                    headline={translate('checkout.payment.messages.missingBasketErrorTitle')}
                    bodyText={translate('checkout.payment.messages.missingBasketErrorText')}
                    cta={{
                        text: translate('checkout.payment.messages.missingBasketErrorRedirectText'),
                        onClick: () =>
                            clientRedirect({
                                targetUrl:
                                    frame?.settings?.pageReferences.basketPage?.url ?? '/basket',
                            }),
                    }}
                />
            );

        if (hasErrorOnPaymentInitiation || paymentInstrumentError)
            return (
                <PaymentMessageBox
                    headline={translate('checkout.payment.messages.paymentInitiationErrorTitle')}
                    bodyText={translate('checkout.payment.messages.paymentInitiationErrorText')}
                    cta={{
                        text: translate(
                            'checkout.payment.messages.paymentInitiationErrorRetryText',
                        ),
                        onClick: () => window.location.reload(),
                    }}
                />
            );

        return null;
    }, [
        initiatedPayment,
        orderNumberGenerationError,
        paymentInstrumentError,
        redirectUrl,
        isGeneratingOrderFlowLinks,
    ]);

    const ctaLabel = translate('checkout.payment.submitText');

    /**
     * The AltaPay credit card form is in an iframe with its own button.
     * We will hide our internal button if the payment is AltaPay credit card/Klarna payment.
     */
    const shouldHideInternalButton = selectedOption && isAltaPayScriptPayment(selectedOption);

    const hasErrors = !!(
        paymentInstrumentError ||
        orderNumberGenerationError ||
        isLoadingError(initiatedPayment)
    );
    const isRedirecting = !!redirectUrl;

    const isButtonDisabled =
        !selectedOption ||
        hasErrors ||
        isRedirecting ||
        isGeneratingOrderFlowLinks ||
        hasInitiatedPayment ||
        isUpdatingAltaPaySession;

    return (
        <StyledCheckoutWrapper>
            <StyledCheckoutMain>
                <div className={styles.paymentFlow}>
                    <div className={styles.header}>
                        <Typography
                            className={styles.headline}
                            variant="title-XS"
                            as="h1"
                            weight="bold"
                        >
                            {translate('checkout.payment.title')}
                        </Typography>
                        <Text variant="bodySmall">{translate('checkout.payment.subtitle')}</Text>
                    </div>

                    {messageBox || (
                        <PaymentOptions
                            enabled={hasUpdatedAltaPaySession}
                            shouldDisableOptions={hasInitiatedPayment}
                            selectedOption={selectedOption}
                            scriptUrl={scriptUrl}
                            onAltaPayScriptComplete={onAltaPayScriptComplete}
                            onPaymentChange={onPaymentChange}
                        />
                    )}

                    {!shouldHideInternalButton && (
                        <StyledCheckoutMobileSubmit
                            className={styles.button}
                            disabled={isButtonDisabled}
                            onClick={onFormSubmission}
                        >
                            {hasInitiatedPayment ? <Spinner /> : ctaLabel}
                        </StyledCheckoutMobileSubmit>
                    )}
                </div>
            </StyledCheckoutMain>

            <StyledCheckoutAside>
                <CheckoutSummary
                    ctaLabel={shouldHideInternalButton ? undefined : ctaLabel}
                    onSubmit={onFormSubmission}
                    isLoading={hasInitiatedPayment}
                    isCtaDisabled={isButtonDisabled}
                />
            </StyledCheckoutAside>
        </StyledCheckoutWrapper>
    );
};

export default PaymentFlow;
